import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import TaxSituationForm from "../../../components/forms/TaxSituationForm";
import { updateFiling } from "../../../api/filings/filingAPI";
import { Handler401 } from "../../../api/utils/responseActions";
import normalizeErrors from "../../../utils/normalizeErrors";
import useFormValidation from "../../../hooks/useFormValidation";
import { buildSchemaMap } from "../../../utils/collectors/GeneralSchemaCollector";
import {
  PTF_SCHEMA,
  BTF_SCHEMA,
} from "../../../utils/schemas/filings/taxSituationSchema";
import { cleanPhoneNumber } from "../../../utils/formatting/phoneFormatter";
import { cleanConditionalFields } from "../../../utils/forms/fieldHelpers";

function TaxSituation({ filingData, onNext, onPrev, schemaKey }) {
  const filingParam = useParams();
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [, setFormData] = useState({});

  const navigate = useNavigate();
  const formMethodsRef = useRef(null);
  const { mapBackendErrors } = useFormValidation(formMethodsRef.current || {});

  // Resolve schema sections for the current service and schemaKey
  const SCHEMA_MAP = useMemo(
    () => buildSchemaMap({ PTF: PTF_SCHEMA, BTF: BTF_SCHEMA }),
    []
  );
  const services = useMemo(
    () => filingData?.tax_service?.services || [],
    [filingData]
  );
  const currentService = useMemo(
    () => (Array.isArray(services) ? services[currentServiceIndex] : null),
    [services, currentServiceIndex]
  );
  const schemaSections = useMemo(
    () =>
      currentService && schemaKey
        ? SCHEMA_MAP[currentService]?.[schemaKey] || []
        : [],
    [SCHEMA_MAP, currentService, schemaKey]
  );

  // Reset when schemaKey changes (e.g., from Tax_Basics to Background)
  useEffect(() => {
    setCurrentServiceIndex(0);
    setFormData({});
  }, [schemaKey]);

  /**
   * Extract only the data for a specific section from the full form state
   * @param {Object} fullFormState - The complete form state with all sections
   * @param {number} sectionIndex - The index of the section to extract
   * @returns {Object} - Only the data for the specified section
   */
  const extractSectionData = (fullFormState, sectionIndex) => {
    if (!Array.isArray(schemaSections) || sectionIndex < 0 || sectionIndex >= schemaSections.length) {
      return {};
    }
    
    const section = schemaSections[sectionIndex];
    if (!section || !Array.isArray(section.fields)) {
      return {};
    }
    
    const sectionData = {};
    
    // Check if this section has a single_address field
    const hasSingleAddress = section.fields.some(f => f && f.type === "single_address");
    const addressFieldNames = ["address", "city", "stateAddress", "zipCode", "suiteAddress", "country"];
    
    // Extract fields for this section
    section.fields.forEach((field) => {
      if (!field || !field.name) return;
      
      const fieldName = field.name;
      
      // For fields with nested structures (multi_boolean_choice, radio_boolean_choice),
      // extract the entire parent object, not individual nested fields
      if (field.type === "multi_boolean_choice" || field.type === "radio_boolean_choice") {
        // Extract the parent field object (e.g., wtwoForm with value and fields)
        if (fullFormState.hasOwnProperty(fieldName)) {
          sectionData[fieldName] = fullFormState[fieldName];
        }
        // Also include flattened nested fields (e.g., "wtwoForm.w2Count")
        // These will be cleaned up by organizeFormData
        Object.keys(fullFormState).forEach((key) => {
          if (key.startsWith(`${fieldName}.`)) {
            sectionData[key] = fullFormState[key];
          }
        });
      } else if (field.type === "single_address") {
        // For single_address, extract the consolidated address object
        if (fullFormState.hasOwnProperty(fieldName)) {
          sectionData[fieldName] = fullFormState[fieldName];
        }
        // Also extract individual address fields if they exist (they'll be cleaned up by organizeFormData)
        // Only extract if this section has a single_address field
        if (hasSingleAddress) {
          addressFieldNames.forEach((addrField) => {
            if (fullFormState.hasOwnProperty(addrField)) {
              sectionData[addrField] = fullFormState[addrField];
            }
          });
        }
      } else {
        // For regular fields, just extract the field value
        // Skip address fields unless this section has a single_address field
        if (addressFieldNames.includes(fieldName) && !hasSingleAddress) {
          return; // Skip address fields that don't belong to this section
        }
        if (fullFormState.hasOwnProperty(fieldName)) {
          sectionData[fieldName] = fullFormState[fieldName];
        }
      }
    });
    
    return sectionData;
  };

  const organizeFormData = (stepData) => {
    // Clean up form data to remove flattened nested fields
    // React-hook-form registers nested fields with dot notation (e.g., "wtwoForm.w2Count", "healthInsurance.marketplace")
    // which creates duplicate fields when we get all values
    const cleanedData = { ...stepData };
    
    // Get all field names that have nested fields (multi_boolean_choice, radio_boolean_choice, single_address)
    // and map them to their nested field names
    const fieldsWithNestedFields = new Map(); // Map<fieldName, Set<nestedFieldNames>>
    const singleAddressFields = new Set(); // Set<fieldName> for single_address fields
    const singleBooleanChoiceFields = new Set(); // Set<fieldName> for single_boolean_choice fields
    
    // Individual address field names that should be removed when a consolidated address object exists
    const addressFieldNames = new Set(["address", "suiteAddress", "city", "stateAddress", "zipCode", "country"]);
    
    // Helper function to recursively collect nested field names
    const collectNestedFieldNames = (field) => {
      const nestedNames = new Set();
      if (field.type === "multi_boolean_choice" && field.fields && Array.isArray(field.fields)) {
        field.fields.forEach(nestedField => {
          nestedNames.add(nestedField.name);
          // Recursively collect nested-nested fields
          if (nestedField.type === "multi_boolean_choice" || nestedField.type === "radio_boolean_choice") {
            const deeperNested = collectNestedFieldNames(nestedField);
            deeperNested.forEach(name => nestedNames.add(name));
          }
        });
      } else if (field.type === "radio_boolean_choice" && field.options && Array.isArray(field.options)) {
        field.options.forEach(option => {
          if (option.fields && Array.isArray(option.fields)) {
            option.fields.forEach(nestedField => {
              nestedNames.add(nestedField.name);
              // Recursively collect nested-nested fields
              if (nestedField.type === "multi_boolean_choice" || nestedField.type === "radio_boolean_choice") {
                const deeperNested = collectNestedFieldNames(nestedField);
                deeperNested.forEach(name => nestedNames.add(name));
              }
            });
          }
        });
      }
      return nestedNames;
    };
    
    // Iterate through schema sections to find fields with nested fields
    schemaSections.forEach((section) => {
      if (Array.isArray(section.fields)) {
        section.fields.forEach((field) => {
          if (field.type === "multi_boolean_choice" || field.type === "radio_boolean_choice") {
            const nestedNames = collectNestedFieldNames(field);
            if (nestedNames.size > 0) {
              fieldsWithNestedFields.set(field.name, nestedNames);
            }
          } else if (field.type === "single_address") {
            // Track single_address fields
            singleAddressFields.add(field.name);
          } else if (field.type === "single_boolean_choice") {
            // Track single_boolean_choice fields
            singleBooleanChoiceFields.add(field.name);
          }
        });
      }
    });
    
    // Move flattened nested fields into their proper nested structure
    // (e.g., "llc_owner.includeBusiness.allTransactionHistory" -> llc_owner.fields.includeBusiness.fields.allTransactionHistory)
    // Then remove the flattened fields
    // First, collect all flattened fields and sort by path length (shorter first) to build structure correctly
    const flattenedFieldsToProcess = [];
    Object.keys(cleanedData).forEach((key) => {
      for (const [parentFieldName] of fieldsWithNestedFields.entries()) {
        if (key.startsWith(`${parentFieldName}.`)) {
          flattenedFieldsToProcess.push({ key, path: key.split('.'), value: cleanedData[key] });
        }
      }
    });
    
    // Sort by path length (shorter paths first) so we build the structure correctly
    flattenedFieldsToProcess.sort((a, b) => a.path.length - b.path.length);
    
    // Process each flattened field
    flattenedFieldsToProcess.forEach(({ key, path, value: fieldValue }) => {
      const parentFieldName = path[0];
      
      // Ensure the parent field exists
      if (!cleanedData[parentFieldName]) {
        cleanedData[parentFieldName] = { value: false, fields: {} };
      }
      if (!cleanedData[parentFieldName].fields) {
        cleanedData[parentFieldName].fields = {};
      }
      
      // Navigate/create the nested structure
      let current = cleanedData[parentFieldName].fields;
      
      // Process each part of the path (skip the first which is the parent field name)
      for (let i = 1; i < path.length; i++) {
        const part = path[i];
        const isLast = i === path.length - 1;
        
        if (isLast) {
          // Last part - this is the actual field, set its value
          // If fieldValue is an object with value and fields, use it directly
          // If fieldValue is a primitive, wrap it in the appropriate structure
          
          if (typeof fieldValue === "object" && fieldValue !== null && !Array.isArray(fieldValue)) {
            // Field value is an object - merge it properly
            if (current[part] && typeof current[part] === "object" && !Array.isArray(current[part])) {
              // Field exists - merge preserving existing value and fields
              // Only update if the new value is not empty/undefined
              const newValue = fieldValue.value !== undefined && fieldValue.value !== "" 
                ? fieldValue.value 
                : current[part].value;
              current[part] = {
                value: newValue,
                fields: {
                  ...(current[part].fields || {}),
                  ...(fieldValue.fields || {}),
                },
              };
            } else {
              // Field doesn't exist - create it
              current[part] = {
                value: fieldValue.value !== undefined ? fieldValue.value : "",
                fields: fieldValue.fields || {},
              };
            }
          } else {
            // Field value is a primitive (boolean, string, number)
            // This means it's a nested field value (e.g., profitAndLoss: true)
            if (current[part] && typeof current[part] === "object" && !Array.isArray(current[part])) {
              // Field exists - update value, preserve fields
              current[part] = {
                ...current[part],
                value: fieldValue,
                fields: current[part].fields || {},
              };
            } else {
              // Field doesn't exist - create it with appropriate structure
              if (typeof fieldValue === "boolean") {
                current[part] = { value: fieldValue };
              } else if (typeof fieldValue === "string" || typeof fieldValue === "number") {
                // For radio_boolean_choice, structure as { value: fieldValue, fields: {} }
                current[part] = { value: fieldValue, fields: {} };
              } else {
                current[part] = { value: fieldValue };
              }
            }
          }
          
        } else {
          // Intermediate part - ensure the nested structure exists
          // If the field already exists, preserve its value and fields (don't overwrite!)
          if (!current[part]) {
            // Field doesn't exist - create it with default structure
            current[part] = { value: false, fields: {} };
          } else if (typeof current[part] === "object" && !Array.isArray(current[part])) {
            // Field exists - preserve its existing value and fields, just ensure structure is correct
            if (!current[part].hasOwnProperty("value")) {
              // Only set default value if it doesn't exist
              current[part].value = false;
            }
            if (!current[part].fields) {
              current[part].fields = {};
            }
            // DO NOT overwrite the value - preserve whatever is already there
          } else {
            // Field exists but is not an object - convert it while preserving the value
            current[part] = { value: current[part], fields: {} };
          }
          // Navigate to the fields of this intermediate field
          current = current[part].fields;
        }
      }
      
      // Remove the flattened field after moving it
      delete cleanedData[key];
    });
    
    // Also remove any remaining flattened fields that weren't processed above
    Object.keys(cleanedData).forEach((key) => {
      for (const [parentFieldName] of fieldsWithNestedFields.entries()) {
        if (key.startsWith(`${parentFieldName}.`)) {
          // This is a flattened nested field - parse the path and move it to the correct location
          const pathParts = key.split('.');
          if (pathParts.length > 1 && pathParts[0] === parentFieldName) {
            // Ensure the parent field exists
            if (!cleanedData[parentFieldName]) {
              cleanedData[parentFieldName] = { value: false, fields: {} };
            }
            if (!cleanedData[parentFieldName].fields) {
              cleanedData[parentFieldName].fields = {};
            }
            
            // Navigate/create the nested structure
            let current = cleanedData[parentFieldName].fields;
            const fieldValue = cleanedData[key];
            
            // Process each part of the path (skip the first which is the parent field name)
            for (let i = 1; i < pathParts.length; i++) {
              const part = pathParts[i];
              const isLast = i === pathParts.length - 1;
              
              if (isLast) {
                // Last part - this is the actual field, set its value
                // If the field already exists, merge with it (preserve existing value and fields)
                if (current[part] && typeof current[part] === "object" && !Array.isArray(current[part])) {
                  // Field already exists - merge the value
                  if (typeof fieldValue === "object" && fieldValue !== null && !Array.isArray(fieldValue)) {
                    // Merge objects, preserving existing fields
                    current[part] = {
                      ...current[part],
                      ...fieldValue,
                      fields: {
                        ...(current[part].fields || {}),
                        ...(fieldValue.fields || {}),
                      },
                    };
                  } else {
                    // Update the value property, preserve existing fields
                    current[part] = {
                      ...current[part],
                      value: fieldValue,
                      fields: current[part].fields || {},
                    };
                  }
                } else {
                  // Field doesn't exist - create it with appropriate structure
                  if (typeof fieldValue === "object" && fieldValue !== null && !Array.isArray(fieldValue)) {
                    current[part] = fieldValue;
                  } else {
                    // Simple value - structure it appropriately
                    if (typeof fieldValue === "boolean") {
                      current[part] = { value: fieldValue };
                    } else if (typeof fieldValue === "string" || typeof fieldValue === "number") {
                      // For radio_boolean_choice or other types, structure as { value: fieldValue, fields: {} }
                      current[part] = { value: fieldValue, fields: {} };
                    } else {
                      current[part] = fieldValue;
                    }
                  }
                }
              } else {
                // Intermediate part - ensure the nested structure exists
                // If the field already exists, preserve its value and fields
                if (!current[part]) {
                  current[part] = { value: false, fields: {} };
                } else if (typeof current[part] === "object" && !Array.isArray(current[part])) {
                  // Field exists - ensure it has the proper structure
                  if (!current[part].hasOwnProperty("value")) {
                    current[part].value = false;
                  }
                  if (!current[part].fields) {
                    current[part].fields = {};
                  }
                } else {
                  // Field exists but is not an object - convert it
                  current[part] = { value: current[part], fields: {} };
                }
                current = current[part].fields;
              }
            }
          }
          
          // Remove the flattened field after moving it
          delete cleanedData[key];
        }
      }
      
      // Also check if the parent field object has duplicate nested fields at the root level
      // (e.g., wtwoForm.w2Count should not exist if wtwoForm.fields.w2Count exists)
      // (e.g., healthInsurance.marketplace should not exist if healthInsurance.fields.marketplace exists)
      if (fieldsWithNestedFields.has(key)) {
        const parentField = cleanedData[key];
        if (parentField && typeof parentField === "object") {
          const nestedFieldNames = fieldsWithNestedFields.get(key);
          
          // Ensure fields object exists
          if (!parentField.fields) {
            parentField.fields = {};
          }
          
          // Remove any duplicate nested fields at the parent level (outside of fields)
          // and move them into fields if they're not already there
          nestedFieldNames.forEach((nestedFieldName) => {
            if (parentField.hasOwnProperty(nestedFieldName) && nestedFieldName !== "value" && nestedFieldName !== "fields") {
              // If the nested field exists at root level but not in fields, move it to fields
              if (!parentField.fields.hasOwnProperty(nestedFieldName)) {
                parentField.fields[nestedFieldName] = parentField[nestedFieldName];
              }
              // Remove from root level
              delete parentField[nestedFieldName];
            }
          });
          
          // Also check for nested fields that might be incorrectly placed in nested structures
          // (e.g., if includeBusiness has allTransactionHistory at the wrong level)
          // Also handle flattened fields like "allTransactionHistory.bookkeepingNeeded"
          if (parentField.fields && typeof parentField.fields === "object") {
            Object.keys(parentField.fields).forEach((nestedKey) => {
              const nestedField = parentField.fields[nestedKey];
              
              // Check if this key is a flattened field (e.g., "allTransactionHistory.bookkeepingNeeded")
              if (nestedKey.includes('.') && nestedKey !== "value" && nestedKey !== "fields") {
                const flattenedPath = nestedKey.split('.');
                if (flattenedPath.length > 1) {
                  // This is a flattened field - move it to the correct nested location
                  const firstPart = flattenedPath[0];
                  const remainingPath = flattenedPath.slice(1);
                  
                  // Ensure the first part exists as a nested field
                  if (!parentField.fields[firstPart]) {
                    parentField.fields[firstPart] = { value: false, fields: {} };
                  } else if (typeof parentField.fields[firstPart] !== "object" || !parentField.fields[firstPart].fields) {
                    parentField.fields[firstPart] = {
                      value: typeof parentField.fields[firstPart] === "object" ? parentField.fields[firstPart].value : parentField.fields[firstPart],
                      fields: {},
                    };
                  }
                  
                  // Navigate to the correct nested location
                  let current = parentField.fields[firstPart].fields;
                  for (let i = 0; i < remainingPath.length; i++) {
                    const part = remainingPath[i];
                    const isLast = i === remainingPath.length - 1;
                    
                    if (isLast) {
                      // Last part - set the value
                      if (typeof nestedField === "object" && nestedField !== null && !Array.isArray(nestedField)) {
                        current[part] = nestedField;
                      } else {
                        current[part] = { value: nestedField };
                      }
                    } else {
                      // Intermediate part - ensure structure exists
                      if (!current[part]) {
                        current[part] = { value: false, fields: {} };
                      } else if (typeof current[part] !== "object" || !current[part].fields) {
                        current[part] = {
                          value: typeof current[part] === "object" ? current[part].value : current[part],
                          fields: {},
                        };
                      }
                      current = current[part].fields;
                    }
                  }
                  
                  // Remove the flattened field
                  delete parentField.fields[nestedKey];
                  return; // Skip further processing for this key
                }
              }
              
              // If this nested field is itself a multi_boolean_choice or radio_boolean_choice,
              // check if it has fields that should be nested deeper
              if (nestedField && typeof nestedField === "object" && nestedField.fields) {
                // Recursively check nested fields for misplaced fields
                const nestedFieldSchema = schemaSections
                  .flatMap(s => s.fields || [])
                  .find(f => f.name === key)?.fields
                  ?.find(f => f.name === nestedKey);
                
                if (nestedFieldSchema && (nestedFieldSchema.type === "multi_boolean_choice" || nestedFieldSchema.type === "radio_boolean_choice")) {
                  // This nested field can have its own nested fields - ensure they're properly nested
                  const nestedNestedFieldNames = new Set();
                  if (nestedFieldSchema.fields && Array.isArray(nestedFieldSchema.fields)) {
                    nestedFieldSchema.fields.forEach(f => nestedNestedFieldNames.add(f.name));
                  }
                  if (nestedFieldSchema.type === "radio_boolean_choice" && nestedFieldSchema.options) {
                    nestedFieldSchema.options.forEach(opt => {
                      if (opt.fields && Array.isArray(opt.fields)) {
                        opt.fields.forEach(f => nestedNestedFieldNames.add(f.name));
                      }
                    });
                  }
                  
                  // Check if any of these nested-nested fields are at the wrong level
                  if (!nestedField.fields) {
                    nestedField.fields = {};
                  }
                  nestedNestedFieldNames.forEach((nestedNestedFieldName) => {
                    if (nestedField.hasOwnProperty(nestedNestedFieldName) && nestedNestedFieldName !== "value" && nestedNestedFieldName !== "fields") {
                      if (!nestedField.fields.hasOwnProperty(nestedNestedFieldName)) {
                        nestedField.fields[nestedNestedFieldName] = nestedField[nestedNestedFieldName];
                      }
                      delete nestedField[nestedNestedFieldName];
                    }
                  });
                }
              }
            });
          }
        }
      }
    });
    
    // Remove individual address fields when a consolidated single_address object exists
    // (e.g., if businessAddress exists, remove address, city, stateAddress, zipCode, suiteAddress, country from root)
    singleAddressFields.forEach((addressFieldName) => {
      if (cleanedData[addressFieldName] && typeof cleanedData[addressFieldName] === "object") {
        // Consolidated address object exists, remove individual address fields from root
        addressFieldNames.forEach((fieldName) => {
          if (cleanedData.hasOwnProperty(fieldName)) {
            delete cleanedData[fieldName];
          }
        });
      }
    });
    
    // Clean up single_boolean_choice fields to only send 'value', remove 'status' if it exists
    // This ensures consistency: only { value: true/false } is sent, not { status: false, value: null }
    // If value is null/undefined, set it to false to ensure API receives a boolean
    // ONLY process fields that are actually in cleanedData (current section's fields)
    Object.keys(cleanedData).forEach((fieldName) => {
      if (singleBooleanChoiceFields.has(fieldName)) {
        if (cleanedData[fieldName] && typeof cleanedData[fieldName] === "object") {
          const fieldData = cleanedData[fieldName];
          // Only keep 'value' property, remove 'status' and any other properties
          // Ensure value is always a boolean (false if null/undefined)
          const booleanValue = fieldData.value != null ? !!fieldData.value : false;
          cleanedData[fieldName] = { value: booleanValue };
        } else {
          // Field exists in current section but value is null/undefined/not present, set to false
          cleanedData[fieldName] = { value: false };
        }
      }
    });
    
    // Helper function to recursively process nested multi_boolean_choice and radio_boolean_choice fields
    const processNestedFields = (fieldData, fieldSchema) => {
      if (!fieldData || typeof fieldData !== "object") return fieldData;
      
      const processedFields = {};
      
      // For radio_boolean_choice, nested fields are in the options, not in fieldSchema.fields
      if (fieldSchema && fieldSchema.type === "radio_boolean_choice" && fieldSchema.options) {
        // Find the selected option and process its nested fields
        const selectedValue = fieldData.value;
        const selectedOption = fieldSchema.options.find(opt => opt.value === selectedValue);
        
        // Collect all possible nested field names from all options to filter them out
        const allNestedFieldNames = new Set();
        fieldSchema.options.forEach((opt) => {
          if (opt.fields && Array.isArray(opt.fields)) {
            opt.fields.forEach((nestedField) => {
              allNestedFieldNames.add(nestedField.name);
            });
          }
        });
        
        if (selectedOption && selectedOption.fields && Array.isArray(selectedOption.fields)) {
          selectedOption.fields.forEach((nestedFieldSchema) => {
            const nestedFieldName = nestedFieldSchema.name;
            const nestedFieldData = fieldData.fields?.[nestedFieldName];
            
            if (nestedFieldSchema.type === "single_boolean_choice") {
              if (nestedFieldData !== undefined && nestedFieldData !== null) {
                // Include the field even if value is false
                processedFields[nestedFieldName] = nestedFieldData;
              }
            } else if (nestedFieldSchema.type === "multi_boolean_choice") {
              if (nestedFieldData && typeof nestedFieldData === "object") {
                const processedNested = processNestedFields(nestedFieldData, nestedFieldSchema);
                processedFields[nestedFieldName] = processedNested;
              }
            } else if (nestedFieldSchema.type === "radio_boolean_choice") {
              if (nestedFieldData && typeof nestedFieldData === "object") {
                const processedNested = processNestedFields(nestedFieldData, nestedFieldSchema);
                processedFields[nestedFieldName] = processedNested;
              }
            } else {
              // For other field types, just copy the value
              if (nestedFieldData !== undefined) {
                processedFields[nestedFieldName] = nestedFieldData;
              }
            }
          });
        }
        
        // Filter out fields from other options - only keep fields from the selected option
        const filteredFields = {};
        if (fieldData.fields && typeof fieldData.fields === "object") {
          Object.keys(fieldData.fields).forEach((key) => {
            // Only include fields that belong to the selected option
            if (processedFields[key] !== undefined) {
              filteredFields[key] = processedFields[key];
            }
          });
        }
        
        // Always preserve the value and only include fields from the selected option
        const result = {
          value: fieldData.value,
        };
        
        if (Object.keys(filteredFields).length > 0) {
          result.fields = filteredFields;
        } else if (Object.keys(processedFields).length > 0) {
          result.fields = processedFields;
        }
        
        return result;
      } else if (fieldData.fields && typeof fieldData.fields === "object" && fieldSchema && fieldSchema.fields) {
        // For multi_boolean_choice and other types, nested fields are in fieldSchema.fields
        fieldSchema.fields.forEach((nestedFieldSchema) => {
          const nestedFieldName = nestedFieldSchema.name;
          const nestedFieldData = fieldData.fields[nestedFieldName];
          
          if (nestedFieldSchema.type === "multi_boolean_choice") {
            // Recursively process nested multi_boolean_choice
            if (nestedFieldData && typeof nestedFieldData === "object") {
              const explicitValue = nestedFieldData.value != null ? !!nestedFieldData.value : null;
              
              if (explicitValue === false) {
                // Explicitly false - don't include fields
                processedFields[nestedFieldName] = { value: false };
              } else {
                // Recursively process nested fields
                const processedNested = processNestedFields(nestedFieldData, nestedFieldSchema);
                
                // Check if processedNested has fields with content
                const hasFieldsWithContent = processedNested.fields && 
                  typeof processedNested.fields === "object" && 
                  Object.keys(processedNested.fields).length > 0 &&
                  Object.values(processedNested.fields).some(val => {
                    if (val === null || val === undefined) return false;
                    if (typeof val === "object") {
                      // For objects, check if they have meaningful content
                      if (val.value !== undefined && val.value !== null && val.value !== false && val.value !== "") {
                        return true;
                      }
                      if (val.fields && typeof val.fields === "object" && Object.keys(val.fields).length > 0) {
                        return true;
                      }
                      return false;
                    }
                    return true;
                  });
                
                if (hasFieldsWithContent || explicitValue === true) {
                  // If fields have content OR value is explicitly true, include fields
                  processedFields[nestedFieldName] = {
                    value: true,
                    fields: processedNested.fields || nestedFieldData.fields || {}
                  };
                } else {
                  // No fields or empty fields - use the value property (default to false if null/undefined)
                  const booleanValue = explicitValue != null ? explicitValue : false;
                  processedFields[nestedFieldName] = { value: booleanValue };
                }
              }
            } else if (nestedFieldData === undefined || nestedFieldData === null) {
              // Field doesn't exist - default to false
              processedFields[nestedFieldName] = { value: false };
            }
          } else if (nestedFieldSchema.type === "radio_boolean_choice") {
            // Process radio_boolean_choice - recursively process its nested fields
            if (nestedFieldData && typeof nestedFieldData === "object") {
              const processedNested = processNestedFields(nestedFieldData, nestedFieldSchema);
              processedFields[nestedFieldName] = processedNested;
            }
          } else {
            // For other field types, just copy the value
            if (nestedFieldData !== undefined) {
              processedFields[nestedFieldName] = nestedFieldData;
            }
          }
        });
      }
      
      // Return the processed field data with processed nested fields
      const result = {
        ...fieldData,
      };
      
      if (Object.keys(processedFields).length > 0) {
        result.fields = processedFields;
      } else if (fieldData.fields && typeof fieldData.fields === "object" && Object.keys(fieldData.fields).length > 0) {
        // If no processing was done but fields exist, preserve them
        result.fields = fieldData.fields;
      }
      
      // Always preserve the value
      if (fieldData.value !== undefined) {
        result.value = fieldData.value;
      }
      
      return result;
    };
    
    // Find field schema by name (recursively search through all sections)
    const findFieldSchema = (fieldName, sections = schemaSections) => {
      for (const section of sections) {
        if (Array.isArray(section.fields)) {
          for (const field of section.fields) {
            if (field.name === fieldName) {
              return field;
            }
            // Recursively search in nested fields
            if (field.fields && Array.isArray(field.fields)) {
              const found = findFieldSchema(fieldName, [{ fields: field.fields }]);
              if (found) return found;
            }
            // Also search in radio_boolean_choice options
            if (field.type === "radio_boolean_choice" && field.options && Array.isArray(field.options)) {
              for (const option of field.options) {
                if (option.fields && Array.isArray(option.fields)) {
                  const found = findFieldSchema(fieldName, [{ fields: option.fields }]);
                  if (found) return found;
                }
              }
            }
          }
        }
      }
      return null;
    };
    
    // Clean up multi_boolean_choice fields to ensure proper structure
    // If value is false, remove 'fields' property. If value is null/undefined, default to { value: false }
    // ONLY process fields that are actually in cleanedData (current section's fields)
    Object.keys(cleanedData).forEach((fieldName) => {
      if (fieldsWithNestedFields.has(fieldName)) {
        // Check if this is a multi_boolean_choice field (has nested fields)
        const fieldSchema = findFieldSchema(fieldName);
        const isMultiBooleanChoice = fieldSchema && fieldSchema.type === "multi_boolean_choice";
        
        if (isMultiBooleanChoice) {
          if (cleanedData[fieldName] && typeof cleanedData[fieldName] === "object") {
            const fieldData = cleanedData[fieldName];
            
            // First, check the explicit value - if it's explicitly false, respect that and clear fields
            const explicitValue = fieldData.value != null ? !!fieldData.value : null;
            
            if (explicitValue === false) {
              // User explicitly unchecked - respect that and don't send fields
              cleanedData[fieldName] = { value: false };
            } else {
              // Process nested fields recursively
              const processed = processNestedFields(fieldData, fieldSchema);
              
              
              // Check if fields have content after processing
              const hasFieldsWithContent = processed.fields && 
                typeof processed.fields === "object" && 
                Object.keys(processed.fields).length > 0 &&
                Object.values(processed.fields).some(val => {
                  if (val === null || val === undefined) return false;
                  if (typeof val === "object") {
                    // For objects, check if they have meaningful content
                    if (val.value !== undefined && val.value !== null && val.value !== false && val.value !== "") {
                      return true;
                    }
                    if (val.fields && typeof val.fields === "object" && Object.keys(val.fields).length > 0) {
                      return true;
                    }
                    return false;
                  }
                  return true;
                });
              
              if (hasFieldsWithContent || explicitValue === true) {
                // If fields have content OR value is explicitly true, include fields
                cleanedData[fieldName] = {
                  value: true,
                  fields: processed.fields || fieldData.fields || {}
                };
              } else {
                // No fields or empty fields - use the value property (default to false if null/undefined)
                const booleanValue = explicitValue != null ? explicitValue : false;
                cleanedData[fieldName] = { value: booleanValue };
              }
              
            }
          }
        }
      }
    });
    
    return cleanedData;
  };

  const taxServices = services;

  // Create a key that changes when addresses are updated to force form regeneration
  // Must be before any early returns to satisfy React Hooks rules
  const formKey = useMemo(() => {
    const serviceType = taxServices[currentServiceIndex];
    const dataPath = schemaKey === "Background" ? "background" : "situation";
    const addresses = filingData?.tax_situation?.details?.[serviceType]?.[dataPath]?.addresses;
    // Use addresses array length and a hash of the first address to detect changes
    const addressesLength = Array.isArray(addresses) ? addresses.length : 0;
    const firstAddressHash = addresses && addresses.length > 0 
      ? `${addresses[0]?.address || ''}-${addresses[0]?.city || ''}-${addresses[0]?.zipCode || ''}` 
      : '';
    return `${schemaKey}-${currentServiceIndex}-${addressesLength}-${firstAddressHash}`;
  }, [schemaKey, currentServiceIndex, filingData?.tax_situation?.details, taxServices]);

  // Only show "No Tax Services" if filingData is loaded but services are empty
  // This prevents the flash during navigation while data is loading
  if (!taxServices.length && filingData?.tax_service) {
    return (
      <div>
        <h2>No Tax Services Selected</h2>
        <p>
          Please go back to the previous step and select at least one tax
          service.
        </p>
      </div>
    );
  }
  
  // If filingData or tax_service isn't loaded yet, return null to avoid flash
  if (!taxServices.length) {
    return null;
  }

  const buildMergedPayload = (serviceType, organizedData, sectionIndex) => {
    // Get field names that belong to the current section ONLY
    const currentSectionFieldNames = new Set();
    const hasSingleAddress = schemaSections[sectionIndex]?.fields?.some(f => f && f.type === "single_address");
    const addressFieldNames = ["address", "city", "stateAddress", "zipCode", "suiteAddress", "country"];
    
    if (sectionIndex != null && schemaSections[sectionIndex] && Array.isArray(schemaSections[sectionIndex].fields)) {
      schemaSections[sectionIndex].fields.forEach(field => {
        if (field && field.name) {
          currentSectionFieldNames.add(field.name);
        }
      });
    }
    
    // Filter organizedData to ONLY include fields from the current section
    const filteredData = {};
    Object.keys(organizedData).forEach((fieldName) => {
      // Include if it's a field from the current section
      // OR if it's an address field and this section has a single_address field
      if (currentSectionFieldNames.has(fieldName) || 
          (hasSingleAddress && addressFieldNames.includes(fieldName))) {
        filteredData[fieldName] = organizedData[fieldName];
      }
    });
    
    // Clean up fields in filtered payload
    const currentSectionFieldTypes = new Map();
    if (sectionIndex != null && schemaSections[sectionIndex] && Array.isArray(schemaSections[sectionIndex].fields)) {
      schemaSections[sectionIndex].fields.forEach(field => {
        if (field && field.name) {
          currentSectionFieldTypes.set(field.name, field.type);
        }
      });
    }
    
    // Clean up single_boolean_choice and multi_boolean_choice fields
    Object.keys(filteredData).forEach((fieldName) => {
      const fieldType = currentSectionFieldTypes.get(fieldName);
      
      if (fieldType === "single_boolean_choice") {
        if (filteredData[fieldName] && typeof filteredData[fieldName] === "object") {
          const fieldData = filteredData[fieldName];
          const booleanValue = fieldData.value != null ? !!fieldData.value : false;
          filteredData[fieldName] = { value: booleanValue };
        }
      } else if (fieldType === "multi_boolean_choice") {
        if (filteredData[fieldName] && typeof filteredData[fieldName] === "object") {
          const fieldData = filteredData[fieldName];
          const explicitValue = fieldData.value != null ? !!fieldData.value : null;
          
          if (explicitValue === false) {
            filteredData[fieldName] = { value: false };
          } else {
            const hasFieldsWithContent = fieldData.fields && 
              typeof fieldData.fields === "object" && 
              Object.keys(fieldData.fields).length > 0 &&
              Object.values(fieldData.fields).some(val => val != null);
            
            if (hasFieldsWithContent) {
              // For cryptoTransaction, add upload_completed: false when checkbox is selected
              let fields = { ...fieldData.fields };
              if (fieldName === "cryptoTransaction" && explicitValue === true) {
                fields.upload_completed = false;
              }
              
              // Clean conditional fields based on schema conditionalDisplay rules
              const currentField = schemaSections[sectionIndex]?.fields?.find(f => f.name === fieldName);
              if (currentField && currentField.fields && Array.isArray(currentField.fields)) {
                fields = cleanConditionalFields(fields, currentField.fields);
              }
              
              filteredData[fieldName] = {
                value: true,
                fields: fields
              };
            } else {
              // For cryptoTransaction, even if no fields with content, add upload_completed: false when checkbox is selected
              if (fieldName === "cryptoTransaction" && explicitValue === true) {
                filteredData[fieldName] = {
                  value: true,
                  fields: { upload_completed: false }
                };
              } else {
                const booleanValue = explicitValue != null ? explicitValue : false;
                filteredData[fieldName] = { value: booleanValue };
              }
            }
          }
        }
      }
    });
    
    // Clean phone number before returning (phone is a simple top-level field)
    if (filteredData.phone && typeof filteredData.phone === "string") {
      filteredData.phone = cleanPhoneNumber(filteredData.phone);
    }
    
    // Convert incomeStates values to uppercase
    if (filteredData.incomeStates && typeof filteredData.incomeStates === "object") {
      const uppercasedIncomeStates = {};
      Object.keys(filteredData.incomeStates).forEach((year) => {
        const states = filteredData.incomeStates[year];
        if (Array.isArray(states)) {
          uppercasedIncomeStates[year] = states.map((s) => 
            typeof s === "string" ? s.toUpperCase() : s
          );
        } else if (typeof states === "string") {
          // Handle single string value
          uppercasedIncomeStates[year] = states.toUpperCase();
        } else {
          uppercasedIncomeStates[year] = states;
        }
      });
      filteredData.incomeStates = uppercasedIncomeStates;
    }
    
    // BTF Background: Convert stateRegistration to uppercase string
    if (filteredData.stateRegistration !== undefined) {
      let stateReg = filteredData.stateRegistration;
      // If it's an array, take the first element
      if (Array.isArray(stateReg)) {
        stateReg = stateReg[0] || "";
      }
      // Convert to uppercase string
      filteredData.stateRegistration = typeof stateReg === "string" 
        ? stateReg.toUpperCase() 
        : String(stateReg).toUpperCase();
    }
    
    // BTF Background: Convert stateForeign to array of uppercase strings
    if (filteredData.stateForeign !== undefined) {
      if (Array.isArray(filteredData.stateForeign)) {
        filteredData.stateForeign = filteredData.stateForeign.map((s) => 
          typeof s === "string" ? s.toUpperCase() : String(s).toUpperCase()
        );
      } else if (typeof filteredData.stateForeign === "string") {
        // If it's a single string, convert to array
        filteredData.stateForeign = [filteredData.stateForeign.toUpperCase()];
      } else {
        filteredData.stateForeign = [];
      }
    }
    
    // BTF Tax Situation: Convert stateAnnualReports to uppercase
    if (filteredData.stateAnnualReport?.fields?.stateAnnualReports) {
      const states = filteredData.stateAnnualReport.fields.stateAnnualReports;
      if (Array.isArray(states)) {
        filteredData.stateAnnualReport.fields.stateAnnualReports = states.map((s) => 
          typeof s === "string" ? s.toUpperCase() : String(s).toUpperCase()
        );
      } else if (typeof states === "string") {
        filteredData.stateAnnualReport.fields.stateAnnualReports = [states.toUpperCase()];
      }
    }
    
    return filteredData;
  };

  // Handler to save after each sub-step completion
  const handleSubStepComplete = async (fullFormState, subStepIndex) => {
    const totalSections = schemaSections.length;
    const isLastSection = subStepIndex >= totalSections - 1;
    // Note: We still need to check API for last section, but handleStepSubmit will also be called
    // This ensures we validate the API response before allowing navigation
    
    // Extract only the current section's data from the full form state
    const sectionData = extractSectionData(fullFormState, subStepIndex);
    
    // Get the section identifier for API validation
    const currentSection = schemaSections[subStepIndex];
    const sectionIdentifier = currentSection?.section || null;
    
    // Save data after each section is completed
    const apiUrl = "tax/filing";
    // Organize only the current section's data
    const organizedData = organizeFormData(sectionData);
    const serviceType = taxServices[currentServiceIndex];
    const envelopeKey =
      schemaKey === "Tax_Basics" ? "tax_situation" : "background_info";
    // Merge only the current section's data with existing data
    const mergedPayload = buildMergedPayload(serviceType, organizedData, subStepIndex);
    
    // Add metadata to track completion and section identifier for API validation
    const payloadWithMetadata = {
      ...mergedPayload,
      _lastCompletedSectionIndex: subStepIndex,
      _isTaxSituationComplete: false,
      _isBackgroundComplete: false,
      // Add section identifier so API can validate only this section
      ...(sectionIdentifier && { 
        [schemaKey === "Tax_Basics" ? "tax_situation_section" : "background_info_section"]: sectionIdentifier 
      })
    };
    
    const requestData = { [envelopeKey]: { ...payloadWithMetadata, serviceType } };

    try {
      const response = await updateFiling(apiUrl, requestData, filingParam.filingUuid);
      
      // Check response status - only allow navigation if status is 200
      if (!response.ok) {
        if (response.status === 401) {
          Handler401(navigate);
          return false;
        }
        
        // Handle error response
        try {
          const err = await response.json();
          // Map field-level errors into RHF
          try {
            mapBackendErrors(err);
          } catch (_) {}
          // Dispatch general wizard error message using normalized errors
          const normalized = normalizeErrors(err);
          const errorMessage =
            (Array.isArray(normalized) && normalized[0]) ||
            err?.message ||
            `Could not save ${schemaKey} data.`;
          document.dispatchEvent(
            new CustomEvent("setWizardError", {
              detail: { error: errorMessage },
            })
          );
        } catch (e) {
          document.dispatchEvent(
            new CustomEvent("setWizardError", {
              detail: { error: response.statusText || "Request failed" },
            })
          );
        }
        return false; // Prevent navigation
      }
      
      // Success - allow navigation
      return true;
    } catch (error) {
      // Network or other errors
      console.error(`[TaxSituation] Error saving sub-step for ${schemaKey}:`, error);
      document.dispatchEvent(
        new CustomEvent("setWizardError", {
          detail: { error: "Network error. Please try again." },
        })
      );
      return false; // Prevent navigation
    }
  };

  const handleStepSubmit = async (fullFormState) => {
    // Wizard only calls on the last sub-step
    // Extract only the last section's data (same as handleSubStepComplete)
    const lastSectionIndex = schemaSections.length - 1;
    const sectionData = extractSectionData(fullFormState, lastSectionIndex);
    
    // Get the section identifier for API validation
    const currentSection = schemaSections[lastSectionIndex];
    const sectionIdentifier = currentSection?.section || null;
    
    setFormData(fullFormState);

    const apiUrl = "tax/filing";
    // Organize only the last section's data
    const organizedData = organizeFormData(sectionData);
    const serviceType = taxServices[currentServiceIndex];
    const envelopeKey =
      schemaKey === "Tax_Basics" ? "tax_situation" : "background_info";
    // Merge only the last section's data with existing data
    const mergedPayload = buildMergedPayload(serviceType, organizedData, lastSectionIndex);
    
    // Add metadata to track completion and section identifier for API validation
    const payloadWithMetadata = {
      ...mergedPayload,
      _lastCompletedSectionIndex: lastSectionIndex,
      _isTaxSituationComplete: schemaKey === "Tax_Basics",
      _isBackgroundComplete: schemaKey === "Background",
      // Add section identifier so API can validate only this section
      ...(sectionIdentifier && { 
        [schemaKey === "Tax_Basics" ? "tax_situation_section" : "background_info_section"]: sectionIdentifier 
      })
    };
    
    const requestData = { [envelopeKey]: { ...payloadWithMetadata, serviceType } };

    try {
      const response = await updateFiling(
        apiUrl,
        requestData,
        filingParam.filingUuid
      );
      if (response.ok) {
        const json = await response.json();
        // Decide next navigation: next service or next main step
        if (currentServiceIndex < taxServices.length - 1) {
          setCurrentServiceIndex((idx) => idx + 1);
          setFormData({});
        } else {
          onNext(json);
        }
      } else {
        if (response.status === 401) {
          Handler401(navigate);
          return;
        }
        try {
          const err = await response.json();
          // Map field-level errors into RHF
          try {
            mapBackendErrors(err);
          } catch (_) {}
          // Dispatch general wizard error message using normalized errors
          const normalized = normalizeErrors(err);
          const errorMessage =
            (Array.isArray(normalized) && normalized[0]) ||
            err?.message ||
            "Could not update filing.";
          document.dispatchEvent(
            new CustomEvent("setWizardError", {
              detail: { error: errorMessage },
            })
          );

          // Optional: navigate to specific sub-step based on backend hint
          try {
            let targetStep = null;
            const sectionHint = err?.section || err?.section_key || null;
            const fieldHint =
              err?.field || (Array.isArray(err?.fields) ? err.fields[0] : null);

            if (sectionHint && Array.isArray(schemaSections)) {
              const idx = schemaSections.findIndex(
                (s) => (s?.section || "") === sectionHint
              );
              if (idx >= 0) targetStep = idx;
            }
            if (
              targetStep === null &&
              fieldHint &&
              Array.isArray(schemaSections)
            ) {
              const idx = schemaSections.findIndex(
                (s) =>
                  Array.isArray(s?.fields) &&
                  s.fields.some((f) => f?.name === fieldHint)
              );
              if (idx >= 0) targetStep = idx;
            }
            if (typeof targetStep === "number" && targetStep >= 0) {
              document.dispatchEvent(
                new CustomEvent("navigateToStep", {
                  detail: { step: targetStep },
                })
              );
            }
          } catch (_) {}
        } catch (e) {
          document.dispatchEvent(
            new CustomEvent("setWizardError", {
              detail: { error: response.statusText || "Request failed" },
            })
          );
        }
      }
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent("setWizardError", {
          detail: { error: "Network error. Please try again." },
        })
      );
    }
  };

  return (
    <div>
      <TaxSituationForm
        key={formKey}
        filing={filingData}
        onSubmit={handleStepSubmit}
        onSubStepComplete={handleSubStepComplete}
        onPrev={onPrev}
        schemaKey={schemaKey}
        service={taxServices[currentServiceIndex]}
        onFormMethodsReady={(fm) => {
          formMethodsRef.current = fm;
        }}
        onValidationChange={() => {}}
      />
    </div>
  );
}

export default TaxSituation;
